import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Checkbox, Field, Input, Select } from '@/app/components/ui/Field';
import { Modal } from '@/app/components/ui/Modal';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { formatCurrency, formatDate } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';
import { AdminSubmitError } from '@/manage/subscriptions/errors';
import {
  getProviderProducts,
  putProviderProduct,
  type ProductPeriodUnit,
  type SubscriptionAdminProduct,
  type SubscriptionProvider,
} from '@/manage/subscriptions/api';

const PROVIDERS: readonly SubscriptionProvider[] = ['apple', 'google', 'mollie'];
const PERIOD_UNITS: readonly ProductPeriodUnit[] = ['day', 'week', 'month', 'year'];

/**
 * "Beheer › Abonnementen › Producten & providers" (§ 2): the tier/period/price
 * mapping per provider-product. `PUT .../products/{provider}/{providerProductId}`
 * writes the current price directly.
 */
export function ProductsPage() {
  const { t } = useTranslation();
  const [provider, setProvider] = useState<SubscriptionProvider>('mollie');
  const [editing, setEditing] = useState<SubscriptionAdminProduct | 'new' | null>(null);

  const products = useAsync(() => getProviderProducts(provider), [provider]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('manageSubscriptions.products.title')}
        lead={t('manageSubscriptions.products.lead')}
        actions={
          <Button size="sm" onClick={() => setEditing('new')}>
            {t('manageSubscriptions.products.newProduct')}
          </Button>
        }
      />

      <Field label={t('manageSubscriptions.products.provider')} className="w-56">
        <Select
          value={provider}
          onChange={(e) => setProvider(e.target.value as SubscriptionProvider)}
        >
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
      </Field>

      <AsyncSection
        state={products}
        label={t('manageSubscriptions.products.title')}
        skeleton="table"
      >
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState text={t('manageSubscriptions.products.empty')} />
          ) : (
            <DataTable
              rowKey={(row) => row.provider_product_id ?? ''}
              rows={rows}
              columns={[
                { key: 'id', header: 'ID', render: (row) => row.provider_product_id },
                {
                  key: 'tier',
                  header: t('manageSubscriptions.products.tier'),
                  render: (row) => row.tier,
                },
                {
                  key: 'period',
                  header: t('manageSubscriptions.products.period'),
                  render: (row) => `${row.period_amount} ${row.period_unit}`,
                },
                {
                  key: 'price',
                  header: t('manageSubscriptions.products.price'),
                  render: (row) =>
                    row.price_amount != null && row.price_currency
                      ? formatCurrency(row.price_amount, row.price_currency)
                      : '—',
                },
                {
                  key: 'active',
                  header: t('manageSubscriptions.products.active'),
                  render: (row) => (
                    <Badge tone={row.active ? 'mint' : 'outline'}>
                      {row.active ? t('manageSubscriptions.products.active') : '—'}
                    </Badge>
                  ),
                },
                {
                  key: 'pending',
                  header: t('manageSubscriptions.products.pending'),
                  render: (row) =>
                    row.pending_price_amount != null && row.pending_price_effective_at
                      ? `${formatCurrency(row.pending_price_amount, row.price_currency ?? 'EUR')} @ ${formatDate(row.pending_price_effective_at)}`
                      : '—',
                },
                {
                  key: 'edit',
                  header: '',
                  align: 'end',
                  render: (row) => (
                    <Button variant="secondary" size="sm" onClick={() => setEditing(row)}>
                      {t('manageSubscriptions.products.edit')}
                    </Button>
                  ),
                },
              ]}
            />
          )
        }
      </AsyncSection>

      <ProductFormModal
        key={editing === 'new' || editing === null ? 'new' : editing.provider_product_id}
        provider={provider}
        product={editing === 'new' ? null : editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          products.reload();
        }}
      />
    </section>
  );
}

function ProductFormModal({
  provider,
  product,
  open,
  onClose,
  onSaved,
}: {
  provider: SubscriptionProvider;
  /** `null` means "new product". */
  product: SubscriptionAdminProduct | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [providerProductId, setProviderProductId] = useState(product?.provider_product_id ?? '');
  const [tier, setTier] = useState(product?.tier ?? '');
  const tierRank = product?.tier_rank ?? 0;
  const [periodAmount, setPeriodAmount] = useState(String(product?.period_amount ?? 1));
  const [periodUnit, setPeriodUnit] = useState<(typeof PERIOD_UNITS)[number]>(
    product?.period_unit ?? 'month',
  );
  const [priceAmount, setPriceAmount] = useState(String(product?.price_amount ?? ''));
  const [priceCurrency, setPriceCurrency] = useState(product?.price_currency ?? 'EUR');
  const [mollieInterval, setMollieInterval] = useState(product?.mollie_interval ?? '');
  const [active, setActive] = useState(product?.active ?? true);

  const save = useSubmit(async () => {
    await putProviderProduct(provider, providerProductId, {
      tier,
      tier_rank: tierRank,
      period_amount: Number(periodAmount),
      period_unit: periodUnit,
      price_amount: priceAmount === '' ? null : Number(priceAmount),
      price_currency: priceAmount === '' ? null : priceCurrency,
      mollie_interval: mollieInterval === '' ? null : mollieInterval,
      active,
    });
    onSaved();
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-xl"
      title={
        product
          ? t('manageSubscriptions.products.formTitle')
          : t('manageSubscriptions.products.formTitleNew')
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save.submit();
        }}
      >
        <p className="text-accent text-[13px]">{t('manageSubscriptions.products.intro')}</p>

        <Field
          label={t('manageSubscriptions.products.fieldProviderProductId')}
          hint={t(`manageSubscriptions.products.fieldProviderProductIdHint_${provider}`)}
        >
          <Input
            value={providerProductId}
            onChange={(e) => setProviderProductId(e.target.value)}
            disabled={product !== null}
            required
          />
        </Field>
        <Field
          label={t('manageSubscriptions.products.fieldTier')}
          hint={t('manageSubscriptions.products.fieldTierHint')}
        >
          <Input value={tier} onChange={(e) => setTier(e.target.value)} required />
        </Field>

        <div className="border-line flex flex-col gap-3 rounded-xl border p-3">
          <p className="text-ink text-sm font-medium">
            {t('manageSubscriptions.products.durationGroupTitle')}
          </p>
          <p className="text-accent text-[13px]">
            {t('manageSubscriptions.products.durationGroupHint')}
          </p>
          <div className="flex gap-3">
            <Field label={t('manageSubscriptions.products.fieldPeriodAmount')} className="w-28">
              <Input
                type="number"
                min={1}
                value={periodAmount}
                onChange={(e) => setPeriodAmount(e.target.value)}
                required
              />
            </Field>
            <Field label={t('manageSubscriptions.products.fieldPeriodUnit')} className="flex-1">
              <Select
                value={periodUnit}
                onChange={(e) => setPeriodUnit(e.target.value as (typeof PERIOD_UNITS)[number])}
              >
                {PERIOD_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {t(`manageSubscriptions.products.periodUnit_${unit}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <div className="border-line flex flex-col gap-3 rounded-xl border p-3">
          <p className="text-ink text-sm font-medium">
            {t('manageSubscriptions.products.priceGroupTitle')}
          </p>
          <p className="text-accent text-[13px]">
            {provider === 'mollie'
              ? t('manageSubscriptions.products.priceGroupHint_mollie')
              : t('manageSubscriptions.products.priceGroupHint_store')}
          </p>
          <div className="flex gap-3">
            <Field label={t('manageSubscriptions.products.fieldPriceAmount')} className="flex-1">
              <Input
                type="number"
                step="0.01"
                value={priceAmount}
                onChange={(e) => setPriceAmount(e.target.value)}
              />
            </Field>
            <Field label={t('manageSubscriptions.products.fieldPriceCurrency')} className="w-24">
              <Input value={priceCurrency} onChange={(e) => setPriceCurrency(e.target.value)} />
            </Field>
          </div>
        </div>

        {provider === 'mollie' && (
          <Field
            label={t('manageSubscriptions.products.fieldMollieInterval')}
            hint={t('manageSubscriptions.products.fieldMollieIntervalHint')}
          >
            <Input value={mollieInterval} onChange={(e) => setMollieInterval(e.target.value)} />
          </Field>
        )}

        <div>
          <Checkbox
            label={t('manageSubscriptions.products.fieldActive')}
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <p className="text-accent mt-1 text-[13px]">
            {t('manageSubscriptions.products.fieldActiveHint')}
          </p>
        </div>

        <div className="mt-2 flex items-center justify-end gap-3">
          <AdminSubmitError error={save.error} />
          <Button type="submit" disabled={save.pending}>
            {t('manageSubscriptions.products.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
